import React, { useEffect, useState, useRef } from 'react';
import { Loader2, CheckCircle, AlertTriangle, CreditCard, XCircle, ExternalLink, X, AlertCircle, Tag } from 'lucide-react';
import { Button } from '../common/Button';
import paymentService from '../../services/paymentService';
import subscriptionService from '../../services/subscriptionService';
import ModalPortal from '../common/ModalPortal';
import ConfirmationModal from '../common/ConfirmationModal';

declare global {
  interface Window {
    Razorpay: any;
  }
}

export enum PaymentStatus {
  PROCESSING = 'processing',
  SUCCESS = 'success',
  ERROR = 'error',
  WAITING = 'waiting',
  PENDING = 'pending'
}

export interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  planId: string;
  planName: string;
  onSuccess: () => void;
  onError: (message: string) => void;
  isRecurring?: boolean;
  couponCode?: string;
  couponDiscount?: number;
  couponFreeMinutes?: number;
}

const PaymentModal: React.FC<PaymentModalProps> = ({
  isOpen,
  onClose,
  planId,
  planName,
  onSuccess,
  onError,
  isRecurring = false,
  couponCode,
  couponDiscount = 0,
  couponFreeMinutes = 0
}) => {
  const [status, setStatus] = useState<PaymentStatus>(PaymentStatus.PROCESSING);
  const [message, setMessage] = useState<string>('');
  const [countdown, setCountdown] = useState<number>(3);
  const [orderId, setOrderId] = useState<string | null>(null); // Used to track subscription status (order=sub_...)
  const [paymentLinkId, setPaymentLinkId] = useState<string | null>(null); // Used to track non recurring/ one time payments (plink_...)
  const [fallbackLink, setFallbackLink] = useState<string | null>(null);
  const [showCancelConfirmation, setShowCancelConfirmation] = useState<boolean>(false);
  const [amount, setAmount] = useState<number>(0);
  const [originalAmount, setOriginalAmount] = useState<number>(0);
  const [processing, setProcessing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [paymentInitiated, setPaymentInitiated] = useState<boolean>(false);
  const [paymentComplete, setPaymentComplete] = useState<boolean>(false);
  const [razorpayOrderId, setRazorpayOrderId] = useState<string | null>(null);
  const [paymentWindowOpened, setPaymentWindowOpened] = useState<boolean>(false);
  const [transactionId, setTransactionId] = useState<string | null>(null);
  
  // Refs to store timeout and interval IDs for cleanup
  const popupCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const countdownTimerRef = useRef<NodeJS.Timeout | null>(null);
  const paymentTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const errorDetectionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Add a ref to track if the component is still mounted
  const isMountedRef = useRef<boolean>(true);

  // Check if Razorpay is loaded
  const isRazorpayLoaded = () => {
    return typeof window.Razorpay !== 'undefined';
  };

  // Load Razorpay script
  useEffect(() => {
    if (!isOpen) return;
    
    // Set mounted flag to true when component mounts or modal opens
    isMountedRef.current = true;

    if (!isRazorpayLoaded()) {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      script.onload = () => {
        // Only proceed if still mounted
        if (isMountedRef.current) {
          console.log('Razorpay script loaded');
          generatePaymentLink();
        }
      };
      document.body.appendChild(script);
    } else {
      generatePaymentLink();
    }

    // Cleanup all timers and intervals on unmount or when modal closes
    return () => {
      // Set mounted flag to false when component unmounts or modal closes
      isMountedRef.current = false;
      cleanupTimers();
    };
  }, [isOpen]);

  // Clean up all timers and intervals
  const cleanupTimers = () => {
    console.log('Cleaning up all timers and intervals');
    
    // Set mounted flag to false to prevent any new API calls
    isMountedRef.current = false;
    
    // Clear popup check interval
    if (popupCheckIntervalRef.current) {
      clearInterval(popupCheckIntervalRef.current);
      popupCheckIntervalRef.current = null;
    }
    
    // Clear countdown timer
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    
    // Clear payment timeout
    if (paymentTimeoutRef.current) {
      clearTimeout(paymentTimeoutRef.current);
      paymentTimeoutRef.current = null;
    }
    
    // Clear error detection timeout
    if (errorDetectionTimeoutRef.current) {
      clearTimeout(errorDetectionTimeoutRef.current);
      errorDetectionTimeoutRef.current = null;
    }
    
    // Reset state
    setStatus(PaymentStatus.PROCESSING);
    setMessage('');
    setCountdown(3);
  };


  // Handle confirmation dialog response
  const handleConfirmCancel = () => {
    setShowCancelConfirmation(false);
    cleanupTimers(); // Clean up all timers before closing
    onClose();
  };

  const handleCancelDismiss = () => {
    setShowCancelConfirmation(false);
  };

  // Process payment using Razorpay
  const generatePaymentLink = async () => {
    try {
      // Check if still mounted before proceeding
      if (!isMountedRef.current) return;
      
      setStatus(PaymentStatus.PROCESSING);
      setMessage('Preparing payment gateway...');
      
      // Use environment variable for Razorpay key
      const razorpayKey = process.env.REACT_APP_RAZORPAY_KEY_ID || '';
      
      // Generate payment link
      const result = await paymentService.processPayment({ 
        planId,
        couponCode 
      });
      
      // Check if still mounted after API call completes
      if (!isMountedRef.current) return;

      if (result.success && result.paymentLink && (result.paymentLinkId || result.orderId)) {
        if (result.paymentLinkId) {
          setPaymentLinkId(result.paymentLinkId);
        }
        if (result.orderId) {
          setOrderId(result.orderId);
        }

        // Store transaction ID if available
        if (result.transactionId) {
          setTransactionId(result.transactionId);
          console.log('Payment transaction ID stored:', result.transactionId);
        }
        
        // Set up error detection for Razorpay API
        errorDetectionTimeoutRef.current = setTimeout(() => {
          // Check if still mounted before updating state
          if (!isMountedRef.current) return;
          
          // If the Razorpay checkout doesn't open or respond within 10 seconds,
          // assume there's an API error and show an error message
          console.error('Possible Razorpay API error: No response after 10 seconds');
          setStatus(PaymentStatus.ERROR);
          setMessage('Unable to connect to the payment gateway. Please try again later or contact support.');
        }, 10000); // 10 second timeout
        
        // Clear the timeout once we proceed with opening the checkout
        try {
          openRazorpayCheckout(result.paymentLink, razorpayKey, result.orderId, result.paymentLinkId);
          if (errorDetectionTimeoutRef.current) {
            clearTimeout(errorDetectionTimeoutRef.current);
            errorDetectionTimeoutRef.current = null;
          }
        } catch (checkoutError) {
          if (errorDetectionTimeoutRef.current) {
            clearTimeout(errorDetectionTimeoutRef.current);
            errorDetectionTimeoutRef.current = null;
          }
          throw checkoutError;
        }
      } else {
        setStatus(PaymentStatus.ERROR);
        const errorMessage = result.message || 'Failed to generate payment link';
        setMessage(errorMessage);
        console.error('PaymentModal: Payment link generation failed:', errorMessage);
        onError(errorMessage);
      }
    } catch (error: any) {
      console.error('PaymentModal: Payment process error:', error);
      // Check if still mounted before updating state
      if (!isMountedRef.current) return;
      
      setStatus(PaymentStatus.ERROR);
      const errorMessage = error.message || 'An unexpected error occurred';
      setMessage(errorMessage);
      console.error('PaymentModal: Payment process error:', error);
      onError(errorMessage);
    }
  };

  // Open Razorpay checkout
  const openRazorpayCheckout = (paymentLink: string, razorpayKey?: string | null, paymentOrderId?: string, _paymentLinkId?: string) => {
    try {
      // Check if still mounted before proceeding
      if (!isMountedRef.current) return;
      
      console.log('Opening payment link in a new window:', paymentLink);
      
      // Use the provided order ID or fall back to the state
      const finalOrderId = paymentOrderId || orderId;
      setOrderId(finalOrderId);
      setPaymentLinkId(_paymentLinkId || paymentLinkId);
      
      // Simply open the payment link in a new window/tab
      // This avoids all the Razorpay SDK complexity
      const paymentWindow = window.open(paymentLink, '_blank', 'width=800,height=600');
      
      if (!paymentWindow) {
        // Check if still mounted before updating state
        if (!isMountedRef.current) return;
        
        // If popup was blocked
        setStatus(PaymentStatus.ERROR);
        setMessage('Popup was blocked. Please enable popups and try again, or click the payment link below.');
        
        // Provide a fallback link that user can click
        setFallbackLink(paymentLink);
        return;
      }
      
      // Set flag that payment window has been opened
      setPaymentWindowOpened(true);
      
      // Get our frontend domain from the current window location
      const frontendDomain = window.location.origin;
      
      // Success and error paths to check for
      const successPath = '/payment/success';
      const errorPath = '/payment/error';
      
      console.log(`Monitoring payment popup for redirect to ${frontendDomain}${successPath} or ${frontendDomain}${errorPath}`);
      
      // Set up an interval to check if the popup has redirected to our frontend success/error page
      popupCheckIntervalRef.current = setInterval(() => {
        // Check if component is still mounted before continuing
        if (!isMountedRef.current) {
          if (popupCheckIntervalRef.current) {
            clearInterval(popupCheckIntervalRef.current);
            popupCheckIntervalRef.current = null;
          }
          
          // Try to close the payment window if it's still open
          if (paymentWindow && !paymentWindow.closed) {
            try {
              paymentWindow.close();
            } catch (e) {
              // Ignore errors when trying to close the window
            }
          }
          
          return;
        }
        
        console.log('PaymentModal: Checking popup status - closed:', paymentWindow.closed);
        
        try {
          // This will throw an error if the popup has navigated to a different origin due to CORS
          // That's expected and we'll catch it
          if (paymentWindow.closed) {
            // Window was closed by user
            console.log('Payment window was closed by user');
            if (popupCheckIntervalRef.current) {
              clearInterval(popupCheckIntervalRef.current);
              popupCheckIntervalRef.current = null;
            }
            
            // Only check payment status if component is still mounted
            if (isMountedRef.current) {
              // Start checking payment status - use paymentLinkId if orderId is not available
              const idToCheck = finalOrderId || _paymentLinkId || paymentLinkId;
              if (idToCheck) {
                console.log('PaymentModal: Window closed, checking payment status with ID:', idToCheck);
                handlePaymentComplete(idToCheck);
              } else {
                console.log('PaymentModal: No order ID or payment link ID available for status check');
                // Still try to check subscription status as fallback
                checkSubscriptionStatus();
              }
            }
            return;
          }
          
          // Try to access the current URL (this may throw a CORS error)
          const currentUrl = paymentWindow.location.href;
          
          // If we can read the URL and it contains our frontend domain with success or error path
          if (currentUrl && (
            currentUrl.includes(`${frontendDomain}${successPath}`) || 
            currentUrl.includes(`${frontendDomain}${errorPath}`)
          )) {
            console.log('Payment popup redirected to frontend URL:', currentUrl);
            const isSuccess = currentUrl.includes(successPath);
            
            if (popupCheckIntervalRef.current) {
              clearInterval(popupCheckIntervalRef.current);
              popupCheckIntervalRef.current = null;
            }
            
            // Close the popup as it's now redirected to our frontend
            paymentWindow.close();
            
            // Only proceed if component is still mounted
            if (!isMountedRef.current) return;
            
            if (isSuccess) {
              // Start checking payment status if redirected to success page
              const idToCheck = finalOrderId || _paymentLinkId || paymentLinkId;
              if (idToCheck) {
                console.log('Detected successful payment redirect, checking payment status with ID:', idToCheck);
                handlePaymentComplete(idToCheck);
              } else {
                console.log('Detected successful payment redirect but no ID available, checking subscription status');
                checkSubscriptionStatus();
              }
            } else {
              // Show error if redirected to error page
              console.log('Detected error payment redirect');
              setStatus(PaymentStatus.ERROR);
              setMessage('Payment processing failed. Please try again or contact support.');
              onError('Redirected to payment error page');
            }
          }
        } catch (error) {
        }
      }, 1000); // Check every second
      
      // Clean up the interval after 3 minutes (absolute maximum we'll check)
      setTimeout(() => {
        if (popupCheckIntervalRef.current) {
          clearInterval(popupCheckIntervalRef.current);
          popupCheckIntervalRef.current = null;
        }
      }, 3 * 60 * 1000);
    } catch (error: any) {
      // Check if still mounted before updating state
      if (!isMountedRef.current) return;
      
      console.error('Error opening payment link:', error);
      setStatus(PaymentStatus.ERROR);
      setMessage(`Failed to open payment gateway: ${error.message || 'Unknown error'}. Please try again.`);
      onError(error.message || 'Failed to open payment gateway');
    }
  };

  // Handle payment completion
  const handlePaymentComplete = async (paymentId: string) => {
    try {
      // Check if still mounted before proceeding
      if (!isMountedRef.current) return;
      
      setStatus(PaymentStatus.PROCESSING);
      setMessage('Verifying payment...');

      // Add a global timeout for the entire payment verification process
      paymentTimeoutRef.current = setTimeout(() => {
        // Check if still mounted before continuing
        if (!isMountedRef.current) return;
        
        console.log('PaymentModal: Global payment verification timeout reached');
        // If we hit the global timeout, we'll still try to check subscription
        // since the payment might have succeeded even if verification timed out
        checkSubscriptionStatus().catch(err => {
          // Check if still mounted before updating state
          if (!isMountedRef.current) return;
          
          console.error('PaymentModal: Final subscription check failed:', err);
          setStatus(PaymentStatus.SUCCESS);
          setMessage('Payment received! Your subscription will be activated shortly.');
          startCountdown();
        });
      }, 30000); // 30 seconds overall timeout
      
      try {
        // Wait a moment for the backend to process the payment callback from Razorpay
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        // Check if still mounted before continuing
        if (!isMountedRef.current) return;
        
        // Determine if this is an order ID or payment link ID
        const isOrderId = paymentId.startsWith('order_') || paymentId.startsWith('sub_');
        const isPaymentLinkId = paymentId.startsWith('plink_');
        
        console.log('PaymentModal: Checking payment status with ID:', paymentId, 'Type:', isOrderId ? 'order' : isPaymentLinkId ? 'payment link' : 'unknown');
        
        // Check payment status - pass the appropriate ID type
        if (isOrderId) {
          await checkPaymentStatus(paymentId, '');
        } else if (isPaymentLinkId) {
          await checkPaymentStatus('', paymentId);
        } else {
          // Fallback: try both ways
          await checkPaymentStatus(paymentId, paymentId);
        }
      } finally {
        // Clear the timeout regardless of success or failure
        if (paymentTimeoutRef.current) {
          clearTimeout(paymentTimeoutRef.current);
          paymentTimeoutRef.current = null;
        }
      }
    } catch (error: any) {
      // Check if still mounted before continuing
      if (!isMountedRef.current) return;
      
      console.error('PaymentModal: Payment verification error:', error);
      
      // Despite the error, we'll try once more to check subscription status
      // This adds resilience in case the payment was successful but status check failed
      try {
        await checkSubscriptionStatus();
      } catch (subscriptionError) {
        // Check if still mounted before updating state
        if (!isMountedRef.current) return;
        
        // Last resort fallback - if we're here, things have gone wrong but the payment
        // might still have succeeded, so assume success but with a notice
        setStatus(PaymentStatus.SUCCESS);
        setMessage('Payment received! It may take a few minutes for your subscription to be fully activated.');
        startCountdown();
      }
    }
  };

  // Check payment status
  // Order id can be order_... or sub_...
  // paymentLinkId will be plink_..
  const checkPaymentStatus = async (orderId: string, paymentLinkId: string) => {
    try {
      // Check if still mounted before proceeding
      if (!isMountedRef.current) return false;
      
      setMessage('Checking payment status...');
      
      console.log('PaymentModal: Starting payment status check with orderId:', orderId, 'paymentLinkId:', paymentLinkId);
      
      // Add maximum retry limit for payment status check
      let statusRetryCount = 0;
      const maxStatusRetries = 5; // Reduced from 10 to 5 for better UX
      
      const pollPaymentStatus = async (): Promise<boolean> => {
        // Check if still mounted before making API call
        if (!isMountedRef.current) return false;
        
        console.log('PaymentModal: Making payment status API call (attempt', statusRetryCount + 1, ')');
        
        // Check the payment status
        const statusResult = await paymentService.checkPaymentStatus(orderId, paymentLinkId);
        
        // Check if still mounted after API call completes
        if (!isMountedRef.current) return false;
        
        console.log('PaymentModal: Payment status response:', statusResult);
        
        // Check if payment is successful
        if (statusResult.success) {
          // The status could be 'completed', 'paid', or another success value
          const status = String(statusResult.status).toLowerCase();
          if (status === 'completed' || status === 'paid') {
            // If payment status confirms success, directly show success
            setStatus(PaymentStatus.SUCCESS);
            setMessage('Payment successful! Your payment is confirmed.');
            startCountdown();
            return true;
          }
        }
        
        // Handle pending or undefined status
        if (!statusResult.status || statusResult.status === 'pending') {
          // If we've hit the max retries, fall back to checking subscription
          statusRetryCount++;
          if (statusRetryCount >= maxStatusRetries) {
            // Check if still mounted before continuing
            if (!isMountedRef.current) return false;
            
            console.log('PaymentModal: Maximum retries reached, checking subscription directly');
            // Try checking subscription as a last resort
            try {
              await checkSubscriptionStatus();
              return true;
            } catch (error) {
              console.error('PaymentModal: Subscription check failed:', error);
              setStatus(PaymentStatus.ERROR);
              setMessage('Payment processing took too long. Your payment may still be processing. Please check your account or contact support.');
              onError('Payment verification timeout after multiple attempts');
              return false;
            }
          }
          
          // Check if still mounted before updating state
          if (!isMountedRef.current) return false;
          
          // If payment is still pending, wait and retry
          setMessage(`Payment is being processed... (attempt ${statusRetryCount}/${maxStatusRetries})`);
          
          // Wait before retrying
          await new Promise(resolve => {
            const timeoutId = setTimeout(() => {
              // Clear timeout reference if component unmounts during the wait
              resolve(true);
            }, 2000);
            
            // Store a reference to this timeout so we can clear it if needed
            const tempTimeoutRef = { current: timeoutId };
            
            // Check if component unmounts during the wait
            const intervalId = setInterval(() => {
              if (!isMountedRef.current) {
                clearTimeout(tempTimeoutRef.current);
                clearInterval(intervalId);
                resolve(false);
              }
            }, 200);
            
            // Cleanup after resolution
            setTimeout(() => clearInterval(intervalId), 2100);
          });
          
          // Check if still mounted before recursively calling
          if (!isMountedRef.current) return false;
          
          return pollPaymentStatus();
        } else {
          // Check if still mounted before updating state
          if (!isMountedRef.current) return false;
          
          // If payment failed
          setStatus(PaymentStatus.ERROR);
          setMessage(statusResult.message || 'Payment verification failed');
          onError(statusResult.message || 'Payment verification failed');
          return false;
        }
      };
      
      // Start polling
      return await pollPaymentStatus();
    } catch (error: any) {
      // Check if still mounted before continuing
      if (!isMountedRef.current) return false;
      
      // If the status check fails, fall back to subscription check
      console.warn('PaymentModal: Error checking payment status, falling back to subscription check:', error);
      await checkSubscriptionStatus();
      return false;
    }
  };

  // Check subscription status
  const checkSubscriptionStatus = async () => {
    try {
      // Check if still mounted before proceeding
      if (!isMountedRef.current) return;
      
      setMessage('Updating subscription status...');
      
      let retryCount = 0;
      const maxRetries = 3; // Reduced from 10 to 3 since we primarily rely on payment status
      const retryInterval = 2000; // 2 seconds between retries
      
      // Store initial subscription state to compare for changes
      let initialSubscription = null;
      try {
        initialSubscription = await subscriptionService.getUserSubscription(true);
        console.log('PaymentModal: Initial subscription state:', initialSubscription);
      } catch (err) {
        console.warn('PaymentModal: Could not fetch initial subscription state', err);
      }
      
      const checkStatus = async (): Promise<boolean> => {
        try {
          // Check if still mounted before making API call
          if (!isMountedRef.current) return false;
          
          // Get latest subscription status
          const subscription = await subscriptionService.getUserSubscription(true); // Force refresh
          
          // Check if still mounted after API call completes
          if (!isMountedRef.current) return false;
          
          console.log('PaymentModal: Checking subscription status, received:', subscription);
          
          // Consider any active subscription as success, not just exact plan match
          // This helps when backend is slow to update or subscription is bundled differently
          if (subscription && subscription.isActive) {
            return true;
          }
          
          // If we have a subscription with this specific plan, that's also success
          if (subscription && subscription.plan && subscription.plan._id === planId) {
            return true;
          }
          
          return false;
        } catch (statusError) {
          console.error('PaymentModal: Error checking subscription status:', statusError);
          return false;
        }
      };
      
      const pollSubscriptionStatus = async () => {
        // Check if still mounted before checking status
        if (!isMountedRef.current) return;
        
        const success = await checkStatus();
        
        if (success) {
          // Check if still mounted before updating state
          if (!isMountedRef.current) return;
          
          setStatus(PaymentStatus.SUCCESS);
          setMessage('Payment successful! Your payment is confirmed.');
          startCountdown();
          return;
        }
        
        retryCount++;
        if (retryCount < maxRetries) {
          // Check if still mounted before updating message
          if (!isMountedRef.current) return;
          
          setMessage(`Updating subscription status... (attempt ${retryCount}/${maxRetries})`);
          
          // Wait before retrying
          await new Promise(resolve => {
            const timeoutId = setTimeout(() => {
              resolve(true);
            }, retryInterval);
            
            // Store a reference to this timeout
            const tempTimeoutRef = { current: timeoutId };
            
            // Check if component unmounts during the wait
            const intervalId = setInterval(() => {
              if (!isMountedRef.current) {
                clearTimeout(tempTimeoutRef.current);
                clearInterval(intervalId);
                resolve(false);
              }
            }, 200);
            
            // Cleanup after resolution
            setTimeout(() => clearInterval(intervalId), retryInterval + 100);
          });
          
          // Check if still mounted before recursive call
          if (!isMountedRef.current) return;
          
          return pollSubscriptionStatus();
        } else {
          // Check if still mounted before updating state
          if (!isMountedRef.current) return;
          
          // Even if we couldn't verify subscription status, the payment was successful
          // So we'll show success message but with a warning
          setStatus(PaymentStatus.SUCCESS);
          setMessage('Payment successful! It may take a few minutes for your subscription to be updated.');
          startCountdown();
        }
      };
      
      await pollSubscriptionStatus();
    } catch (error: any) {
      // Check if still mounted before updating state
      if (!isMountedRef.current) return;
      
      console.error('PaymentModal: Subscription status check error:', error);
      
      // If we can't verify subscription status but payment was successful,
      // still consider it a success but with a note
      setStatus(PaymentStatus.SUCCESS);
      setMessage('Payment successful! It may take a few minutes for your subscription to be fully activated.');
      startCountdown();
    }
  };

  // Start countdown for auto-close
  const startCountdown = () => {
    // Check if still mounted before starting countdown
    if (!isMountedRef.current) return;
    
    // Clear any existing countdown timer
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
    }
    
    countdownTimerRef.current = setInterval(() => {
      // Check if still mounted before updating state
      if (!isMountedRef.current) {
        if (countdownTimerRef.current) {
          clearInterval(countdownTimerRef.current);
          countdownTimerRef.current = null;
        }
        return;
      }
      
      setCountdown((prev) => {
        if (prev <= 1) {
          if (countdownTimerRef.current) {
            clearInterval(countdownTimerRef.current);
            countdownTimerRef.current = null;
          }
          onSuccess();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Get title based on current status
  const getTitle = () => {
    switch (status) {
      case PaymentStatus.PROCESSING:
        return 'Processing Payment';
      case PaymentStatus.PENDING:
        return 'Pending Payment';
      case PaymentStatus.WAITING:
        return 'Waiting for Payment';
      case PaymentStatus.SUCCESS:
        return 'Payment Successful';
      case PaymentStatus.ERROR:
        return 'Payment Failed';
      default:
        return 'Subscription Payment';
    }
  };

  // Get icon based on current status
  const getIcon = () => {
    switch (status) {
      case PaymentStatus.PROCESSING:
      case PaymentStatus.PENDING:
      case PaymentStatus.WAITING:
        return <CreditCard className="w-5 h-5 text-primary-light dark:text-primary-dark" />;
      case PaymentStatus.SUCCESS:
        return <CheckCircle className="w-5 h-5 text-green-500 dark:text-green-400" />;
      case PaymentStatus.ERROR:
        return <AlertTriangle className="w-5 h-5 text-red-500 dark:text-red-400" />;
      default:
        return <CreditCard className="w-5 h-5 text-primary-light dark:text-primary-dark" />;
    }
  };

  // Add a handleTryAgain function above the render section
  const handleTryAgain = () => {
    // Reset state before trying again
    cleanupTimers(); // Clean up all timers
    setStatus(PaymentStatus.PROCESSING);
    setMessage('');
    setFallbackLink(null);
    
    // Generate a new payment link
    generatePaymentLink();
  };

  // Handle manual payment status check
  const handleCheckPaymentStatus = async () => {
    // Use the same logic as window close handler
    const idToCheck = orderId || paymentLinkId;
    if (!idToCheck) {
      console.log('No orderID and paymentLinkID available for status check');
      return;
    }

    try {
      setStatus(PaymentStatus.PROCESSING);
      setMessage('Checking payment status...');
      
      console.log('PaymentModal: Manual payment status check with ID:', idToCheck);
      
      // Use the same logic as handlePaymentComplete
      const isOrderId = idToCheck.startsWith('order_') || idToCheck.startsWith('sub_');
      const isPaymentLinkId = idToCheck.startsWith('plink_');
      
      if (isOrderId) {
        await checkPaymentStatus(idToCheck, '');
      } else if (isPaymentLinkId) {
        await checkPaymentStatus('', idToCheck);
      } else {
        // Fallback: try both ways
        await checkPaymentStatus(idToCheck, idToCheck);
      }
    } catch (error: any) {
      console.error('Error checking payment status:', error);
      // If status check fails, try subscription check
      try {
        await checkSubscriptionStatus();
      } catch (subscriptionError) {
        setStatus(PaymentStatus.ERROR);
        setMessage('Unable to verify payment status. Please try again or contact support.');
        onError('Payment status check failed');
      }
    }
  };

  // Handle payment cancellation
  const handleCancelPayment = async () => {
    if (!transactionId) {
      console.log('No transaction ID available for cancellation');
      onClose();
      return;
    }

    try {
      console.log('Cancelling payment for transaction ID:', transactionId);
      
      // Call cancel payment API endpoint
      const response = await paymentService.cancelPayment(transactionId);
      
      if (response.success) {
        console.log('Payment cancelled successfully');
        setStatus(PaymentStatus.ERROR);
        setMessage('Payment cancelled successfully.');
        
        // Close modal after a short delay
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        throw new Error(response.message || 'Failed to cancel payment');
      }
    } catch (error: any) {
      console.error('Error cancelling payment:', error);
      setMessage('Unable to cancel payment. Please contact support if needed.');
      
      // Still close the modal as user wanted to cancel
      setTimeout(() => {
        onClose();
      }, 2000);
    }
  };

  // Handle payment success
  const handlePaymentSuccess = () => {
    setPaymentComplete(true);
    onSuccess();
  };
  
  // Handle payment failure
  const handlePaymentFailure = (message: string) => {
    setError(message || 'Payment verification failed');
    onError(message || 'Payment verification failed');
  };
  
  // Handle confirmation of payment
  const handleConfirmPayment = async () => {
    setProcessing(true);
    
    try {
      // Simulate payment verification - in real implementation this would be a server check
      await new Promise(resolve => setTimeout(resolve, 1500));
      handlePaymentSuccess();
    } catch (error: any) {
      handlePaymentFailure(error.message || 'Payment verification failed');
    } finally {
      setProcessing(false);
    }
  };
  
  // Handle closing the modal
  const handleClose = () => {
    if (!processing) {
      onClose();
    }
  };

  // Define handleOpenPaymentWindow function
  const handleOpenPaymentWindow = () => {
    if (fallbackLink) {
      window.open(fallbackLink, '_blank', 'width=800,height=600');
    } else if (orderId) {
      generatePaymentLink();
    }
  };

  if (!isOpen) return null;

  // Calculate the savings from coupon if applied
  const hasCouponDiscount = couponDiscount > 0;
  const displayOriginalAmount = hasCouponDiscount ? originalAmount : 0;
  
  return (
    <ModalPortal>
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in z-[100]">
        <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-xl max-w-md w-full border border-border-light dark:border-border-dark animate-fade-in-up">
          {/* Header */}
          <div className="p-5 border-b border-border-light dark:border-border-dark">
            <div className="flex items-center">
              <div className="p-2 rounded-full bg-primary-light/10 dark:bg-primary-dark/10 mr-3">
                {getIcon()}
              </div>
              <h3 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark">
                {getTitle()}
              </h3>
            </div>
          </div>

          {/* Content */}
          <div className="p-5">
            {status === PaymentStatus.PROCESSING && (
              <div className="flex flex-col items-center justify-center py-6">
                <div className="w-16 h-16 rounded-full bg-primary-light/10 dark:bg-primary-dark/10 flex items-center justify-center mb-5">
                  <Loader2 className="w-9 h-9 text-primary-light dark:text-primary-dark animate-spin" />
                </div>
                <p className="text-text-secondary-light dark:text-text-secondary-dark text-center text-base">
                  Preparing your payment for <span className="font-medium text-text-primary-light dark:text-text-primary-dark">{planName}</span>
                </p>
                <p className="text-text-secondary-light dark:text-text-secondary-dark text-sm text-center mt-2 mb-6">
                  You will be redirected to our secure payment gateway shortly.
                  {isRecurring && <span className="text-text-secondary-light dark:text-text-secondary-dark text-sm text-center mt-2 mb-6">
                   <br />
                  <b>NOTE:</b> Please close payment gateway popup manually after payment is successful incase the popup doesn't close automatically.
                  </span>}
                </p>
                <div className="flex flex-col md:flex-row justify-end gap-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={paymentWindowOpened ? handleCheckPaymentStatus : handleCancelPayment}
                    className="mt-0"
                  >
                    {paymentWindowOpened ? "I've Completed Payment" : 'Cancel Payment'}
                  </Button>
                  {paymentWindowOpened && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCancelPayment}
                      className="mt-0"
                    >
                      Cancel Payment
                    </Button>
                  )}
                </div>
              </div>
            )}

            {status === PaymentStatus.WAITING || status === PaymentStatus.PENDING && (
              <div className="flex flex-col">
                
                <div className="mt-4 border-t border-border-light dark:border-border-dark pt-4">
                  <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-4">
                    After completing your payment:
                  </p>
                  <Button
                    variant="secondary"
                    fullWidth={true}
                    rightIcon={<CheckCircle size={16} />}
                    onClick={() => {
                      setStatus(PaymentStatus.SUCCESS);
                      onSuccess();
                    }}
                  >
                    I've Completed Payment
                  </Button>
                </div>
              </div>
            )}

            {status === PaymentStatus.SUCCESS && (
              <div className="flex flex-col items-center justify-center py-6">
                <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-800/30 flex items-center justify-center mb-5">
                  <CheckCircle className="w-9 h-9 text-green-600 dark:text-green-400" />
                </div>
                <p className="text-text-secondary-light dark:text-text-secondary-dark text-center text-base mb-2">
                  Your payment for <span className="font-medium text-green-600 dark:text-green-400">{planName}</span> has been completed.
                </p>
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/30 rounded-lg px-4 py-3 mt-3 w-full">
                  <p className="text-green-700 dark:text-green-300 text-sm text-center">
                    Closing automatically in <span className="font-medium">{countdown}</span> seconds...
                  </p>
                </div>
              </div>
            )}

            {status === PaymentStatus.ERROR && (
              <div className="text-center">
                <div className="mb-6">
                  <div className="mx-auto flex items-center justify-center w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full mb-4">
                    <XCircle className="w-8 h-8 text-red-500 dark:text-red-400" />
                  </div>
                  <h3 className="text-lg font-medium text-text-primary-light dark:text-text-primary-dark mb-2">
                    Payment Failed
                  </h3>
                  <p className="text-base text-text-secondary-light dark:text-text-secondary-dark mb-4">
                    {error || 'There was an issue processing your payment. Please try again.'}
                  </p>
                  {fallbackLink && (
                    <div className="mb-4">
                      <a 
                        href={fallbackLink} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-primary-light dark:text-primary-dark flex items-center justify-center hover:underline"
                      >
                        <ExternalLink size={16} className="mr-1" />
                        Open Payment Page Manually
                      </a>
                    </div>
                  )}
                </div>
                <div className="flex flex-row justify-end gap-2">
                  <Button
                    onClick={onClose}
                    variant="outline"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleTryAgain}
                    variant="primary"
                  >
                    Try Again
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Confirmation modal for canceling the payment */}
      <ConfirmationModal
        isOpen={showCancelConfirmation}
        title="Cancel Payment?"
        message="Are you sure you want to cancel this payment? You can come back and purchase the plan later."
        confirmText="Yes, Cancel Payment"
        cancelText="No, Continue Payment"
        confirmVariant="danger"
        onConfirm={handleConfirmCancel}
        onCancel={handleCancelDismiss}
        icon={<AlertTriangle className="w-5 h-5 text-red-500" />}
      />
    </ModalPortal>
  );
};

export default PaymentModal; 